import { useEffect, useState } from "react";
import { Contact as ContactIcon, Plus, Star, Power, Pencil, Mail, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Contacts() {
  const [config, setConfig] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ receiving_line: "", location: "", name: "", email: "", is_fallback: false });
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ receiving_line: "", location: "", name: "", email: "" });
  const [testingId, setTestingId] = useState(null);

  const sendTest = async (c) => {
    setTestingId(c.id);
    try {
      const { data } = await api.post(`/contacts/${c.id}/test-email`);
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send test email");
    } finally { setTestingId(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts");
      setContacts(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)); load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, email: form.email.trim().toLowerCase(), location: form.location || undefined };
      await api.post("/contacts", payload);
      toast.success("Contact added");
      setForm({ receiving_line: "", location: "", name: "", email: "", is_fallback: false });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Add failed"); }
    finally { setBusy(false); }
  };

  const patch = async (c, updates) => {
    try { await api.put(`/contacts/${c.id}`, updates); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };
  const toggleActive = async (c) => {
    if (c.active) { await api.delete(`/contacts/${c.id}`).then(load).catch(() => toast.error("Failed")); }
    else { await patch(c, { active: true }); }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setEditForm({ receiving_line: c.receiving_line || "", location: c.location || "", name: c.name || "", email: c.email || "" });
  };
  const saveEdit = async (c) => {
    if (!editForm.receiving_line || !editForm.name.trim() || !editForm.email.trim()) {
      toast.error("Line, name and email are required"); return;
    }
    await patch(c, {
      receiving_line: editForm.receiving_line,
      location: editForm.location,
      name: editForm.name.trim(),
      email: editForm.email.trim().toLowerCase(),
    });
    setEditId(null);
  };

  if (!config) return <div className="p-6 font-mono text-sm text-muted-foreground">Loading…</div>;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-6 space-y-5 max-w-5xl" data-testid="contacts-page">
      <div>
        <h1 className="font-head font-black text-3xl tracking-tight flex items-center gap-2">
          <ContactIcon className="w-7 h-7 text-cyan-400" /> Contacts Directory
        </h1>
        <p className="text-sm text-muted-foreground font-mono mt-1">Auto-fills the receiving contact at placement and keeps reminders reaching the right person. Mark a fallback per line for escalations.</p>
      </div>

      <form onSubmit={add} className="glass glass-cyan rounded-xl p-4" data-testid="contact-form">
        <div className="flex items-center gap-2 mb-3"><Plus className="w-4 h-4 text-cyan-400" />
          <h2 className="font-head font-bold text-sm uppercase tracking-wide">Add a contact</h2></div>
        <div className="grid md:grid-cols-6 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Receiving Line</Label>
            <select value={form.receiving_line} onChange={(e) => set("receiving_line")(e.target.value)} required data-testid="contact-line"
                    className="w-full h-10 rounded-md bg-slate-900/60 border border-white/10 px-2 text-sm">
              <option value="">Select…</option>
              {config.receiving_lines.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Location (opt.)</Label>
            <select value={form.location} onChange={(e) => set("location")(e.target.value)} data-testid="contact-location"
                    className="w-full h-10 rounded-md bg-slate-900/60 border border-white/10 px-2 text-sm">
              <option value="">Any</option>
              {config.locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Name</Label>
            <Input value={form.name} onChange={(e) => set("name")(e.target.value)} required data-testid="contact-name" className="bg-slate-900/60 border-white/10 h-10" />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} required data-testid="contact-email" className="bg-slate-900/60 border-white/10 h-10" />
          </div>
          <Button type="submit" disabled={busy} data-testid="contact-submit" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold h-10">
            {busy ? "Adding…" : "Add"}
          </Button>
        </div>
        <label className="flex items-center gap-2 mt-3 text-xs font-mono text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={form.is_fallback} onChange={(e) => set("is_fallback")(e.target.checked)} data-testid="contact-fallback" />
          Fallback for this line (receives escalations when a referral isn't confirmed)
        </label>
      </form>

      <div className="glass rounded-xl p-4" data-testid="contacts-list">
        <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground mb-3">Directory ({contacts.length})</h2>
        {loading ? <p className="text-xs text-muted-foreground font-mono">Loading…</p> : (
          <div className="space-y-1.5">
            {contacts.length === 0 && <p className="text-xs text-muted-foreground font-mono">No contacts yet — add your receiving contacts above.</p>}
            {contacts.map((c) => (
              <div key={c.id} data-testid={`contact-row-${c.id}`}
                   className={`rounded-md border px-3 py-2 ${c.active ? "border-white/8" : "border-white/5 opacity-50"}`}>
                {editId === c.id ? (
                  <div className="grid md:grid-cols-6 gap-2 items-end" data-testid={`contact-edit-${c.id}`}>
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase text-muted-foreground font-mono">Line</Label>
                      <select value={editForm.receiving_line} onChange={(e) => setEditForm((f) => ({ ...f, receiving_line: e.target.value }))} data-testid={`edit-line-${c.id}`}
                              className="w-full h-9 rounded-md bg-slate-900/60 border border-white/10 px-2 text-sm">
                        {config.receiving_lines.map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase text-muted-foreground font-mono">Location</Label>
                      <select value={editForm.location} onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))} data-testid={`edit-location-${c.id}`}
                              className="w-full h-9 rounded-md bg-slate-900/60 border border-white/10 px-2 text-sm">
                        <option value="">Any</option>
                        {config.locations.map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase text-muted-foreground font-mono">Name</Label>
                      <Input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid={`edit-name-${c.id}`} className="bg-slate-900/60 border-white/10 h-9" />
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-[11px] uppercase text-muted-foreground font-mono">Email</Label>
                      <Input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} data-testid={`edit-email-${c.id}`} className="bg-slate-900/60 border-white/10 h-9" />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => saveEdit(c)} data-testid={`edit-save-${c.id}`} className="h-9 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-xs">Save</Button>
                      <Button onClick={() => setEditId(null)} variant="ghost" data-testid={`edit-cancel-${c.id}`} className="h-9 text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-[160px]">
                      <div className="text-sm font-medium flex items-center gap-2">{c.name}
                        {c.is_fallback && <span className="text-[10px] font-mono text-amber-300 border border-amber-500/40 rounded px-1 flex items-center gap-0.5"><Star className="w-2.5 h-2.5" />FALLBACK</span>}
                        {c.receives_digest && <span className="text-[10px] font-mono text-cyan-300 border border-cyan-500/40 rounded px-1 flex items-center gap-0.5"><BellRing className="w-2.5 h-2.5" />DIGEST</span>}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono truncate">{c.email}</div>
                    </div>
                    <span className="text-xs font-mono text-cyan-300">{c.receiving_line}{c.location ? ` · ${c.location}` : " · any"}</span>
                    <div className="flex items-center gap-3">
                      <button onClick={() => sendTest(c)} disabled={testingId === c.id || !c.active} data-testid={`contact-test-${c.id}`}
                              className="text-xs font-mono text-emerald-300 hover:text-emerald-200 flex items-center gap-1 disabled:opacity-40">
                        <Mail className="w-3.5 h-3.5" /> {testingId === c.id ? "Sending…" : "Test email"}
                      </button>
                      <button onClick={() => startEdit(c)} data-testid={`contact-edit-btn-${c.id}`}
                              className="text-xs font-mono text-cyan-300 hover:text-cyan-200 flex items-center gap-1">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => patch(c, { is_fallback: !c.is_fallback })} data-testid={`contact-fallback-${c.id}`}
                              className="text-xs font-mono text-amber-300 hover:text-amber-200 flex items-center gap-1">
                        <Star className="w-3.5 h-3.5" /> {c.is_fallback ? "Unset" : "Set"} fallback
                      </button>
                      <button onClick={() => patch(c, { receives_digest: !c.receives_digest })} data-testid={`contact-digest-${c.id}`}
                              className="text-xs font-mono text-cyan-300 hover:text-cyan-200 flex items-center gap-1">
                        <BellRing className="w-3.5 h-3.5" /> {c.receives_digest ? "Remove from digest" : "Add to digest"}
                      </button>
                      <button onClick={() => toggleActive(c)} data-testid={`contact-active-${c.id}`}
                              className={`text-xs font-mono flex items-center gap-1 ${c.active ? "text-red-300 hover:text-red-200" : "text-emerald-300 hover:text-emerald-200"}`}>
                        <Power className="w-3.5 h-3.5" /> {c.active ? "Deactivate" : "Reactivate"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
